/**
 * SCHEDULE — the view model both trees read (migration 3071; DESIGN-CHANGELOG
 * 2026-10-14). The app's answer to `design/gt-sched-vm.js`, the one view
 * model the two mirrors' screen groups (`GT Schedule.dc.html`, `GT Mobile
 * Schedule.dc.html`) share. Copy is theirs verbatim; every computed value is
 * evaluated here from `lib/schedule.ts` and the store.
 *
 * Two things differ from the mirror by construction, not by taste:
 *  - ART. The mirror bakes a cover into a CSS `face`; the app resolves cover
 *    art through `useArt`, a hook, so a nomination carries `coverCands` + its
 *    `fall` gradient and the component paints it.
 *  - HANDLERS. The mirror's `pick`/`open`/slider closures call its shell; here
 *    the components call store actions with the ids this model hands them.
 *
 * `now` is the LOCAL clock, on purpose: a night and its NOW line are facts
 * about the room the crew is sitting in, which is where this device is.
 */

import { ago, chipOf, fallGradient, initialsOf } from '../lib/format.ts';
import { inkOn } from '../lib/color.ts';
import { artCands, isCall, LVL } from '../lib/logic.ts';
import { rTier, type RoundTier } from '../lib/round.ts';
import {
  hourIdx,
  idxHour,
  SC_HRS,
  SC_MONTH,
  SC_NH,
  scDayTxt,
  scFits,
  scKey,
  scMonth,
  scNowI,
  scOnWheel,
  scRangeTxt,
  type SchedNom,
} from '../lib/schedule.ts';
import type { ArtCand, User } from '../lib/types.ts';
import type { QdStore } from './types.ts';

/** Phone guide: px per hour, and the pinned player column. Desktop is fluid %. */
export const SCH_HPW = 48;
export const SCH_GUT_M = 64;
export const SCH_GUT_D = 76;

const TCLR: Record<RoundTier, string> = {
  s: 'var(--acc)',
  a: 'var(--gt-text)',
  b: 'var(--gt-text-5)',
  c: 'var(--acc2)',
};

/** The weekday row, Monday first; weekend columns read brighter. */
export const SCH_HEAD = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((l, i) => ({
  key: l,
  l,
  l1: l[0] ?? '',
  clr: i < 4 ? 'var(--gt-ghost)' : 'var(--gt-text-5)',
}));

/** A small square face: a backer, a nominator, a party row. */
export interface SchFaceVM {
  key: string;
  ini: string;
  bg: string;
  ink: string;
  nm: string;
  hasAv: boolean;
  avBg: string;
  /** overlap in a stack */
  ml: string;
  bd: string;
  z: number;
}

/** A nomination, decorated with what renders it (the mirror's `deco()`). */
export interface SchNomVM {
  key: string;
  id: number;
  nm: string;
  wn: string;
  ini: string;
  fall: string;
  coverCands: readonly ArtCand[];
  tier: RoundTier;
  tLet: string;
  tClr: string;
  by: string;
  byF: SchFaceVM;
  byTxt: string;
  mine: boolean;
  bys: string[];
  qMine: boolean;
  qFaces: SchFaceVM[];
  cnt: number;
  from: number | null;
  to: number | null;
  fromI: number;
  toI: number;
  any: boolean;
  live: boolean;
  passed: boolean;
  openEnd: boolean;
  range: string;
  rangeLive: string;
  rClr: string;
  bd: string;
  /** an open UNTIL is a dashed right edge */
  bRst: 'dashed' | 'solid';
  sh: string;
  op: string;
}

const pct = (i: number): string => ((i / SC_NH) * 100).toFixed(3) + '%';

function nowTxtOf(now: Date): string {
  let h = now.getHours();
  const m = now.getMinutes();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return h + ':' + String(m).padStart(2, '0') + ' ' + ap;
}

export function scheduleVM(s: QdStore, now: Date = new Date()) {
  const me = s.me();
  const meK = me ? me.k : '';
  const nowMs = now.getTime();
  const todayK = scKey(now);
  const nowI = scNowI(now);
  /** The roster the tiers are read from: the whole party — nobody is seated yet. */
  const roster: readonly User[] = s.users;
  const sched = s.sched;

  const nameOf = (k: string): string => {
    const u = s.user(k);
    return u ? u.name.toUpperCase() : 'FORMER MEMBER';
  };

  const face = (k: string, i: number): SchFaceVM => {
    const u = s.user(k);
    const c = chipOf(u);
    const mine = k === meK;
    return {
      key: k,
      ini: c.ini,
      bg: c.hueBg,
      ink: inkOn(c.hueBg),
      nm: mine ? 'YOU' : nameOf(k),
      hasAv: c.hasAv,
      avBg: c.avBg,
      ml: i ? '-5px' : '0',
      bd: mine ? 'var(--acc)' : 'var(--gt-ink)',
      z: mine ? 2 : 1,
    };
  };

  const deco = (n: SchedNom, k: string): SchNomVM | null => {
    const g = s.gById(n.id);
    if (!g) return null;
    const e = s.entryOf(n.id);
    const tier = rTier(e?.votes, roster);
    const bys = (n.bys.length ? n.bys : [n.by]).filter((b) => !!s.user(b));
    const isToday = k === todayK;
    const live = isToday && scFits(n, nowI);
    const passed = isToday && n.to != null && hourIdx(n.to) <= nowI;
    const any = n.from == null && n.to == null;
    const range = scRangeTxt(n.from, n.to);
    return {
      key: k + '-' + n.id,
      id: n.id,
      nm: g.n,
      wn: g.n.toUpperCase(),
      ini: initialsOf(g.n),
      fall: fallGradient(g.id),
      coverCands: artCands(g, 'p', s.assets),
      tier,
      tLet: tier.toUpperCase(),
      tClr: TCLR[tier],
      by: n.by,
      byF: face(n.by, 0),
      byTxt: (n.by === meK ? 'YOU' : nameOf(n.by)) + ' · ' + ago(n.at, nowMs),
      mine: n.by === meK,
      bys,
      qMine: bys.includes(meK),
      qFaces: bys.map(face),
      cnt: bys.length,
      from: n.from,
      to: n.to,
      fromI: n.from == null ? 0 : hourIdx(n.from),
      toI: n.to == null ? SC_NH : hourIdx(n.to),
      any,
      live,
      passed,
      openEnd: n.to == null && !any,
      range,
      rangeLive: live ? '◆ ' + (any ? 'NOW' : range) : range,
      rClr: live ? 'var(--acc)' : passed || any ? 'var(--gt-muted-2)' : 'var(--gt-text-4)',
      bd: live ? 'var(--acc)' : passed || any ? 'var(--gt-border)' : 'var(--gt-border-2)',
      bRst: n.to == null && !any ? 'dashed' : 'solid',
      sh: live ? 'var(--gt-shadow-btn)' : 'none',
      op: passed ? '.45' : '1',
    };
  };

  const nightOf = (k: string) => {
    const noms = (sched[k] ?? []).map((n) => deco(n, k)).filter((x): x is SchNomVM => !!x);
    const who: string[] = [];
    noms.forEach((n) => n.bys.forEach((b) => {
      if (!who.includes(b)) who.push(b);
    }));
    return { k, noms, who, today: k === todayK, past: k < todayK };
  };

  // ---- the month ----------------------------------------------------------
  const y = s.schY;
  const m = s.schM;
  const weeks = scMonth(y, m).map((row, wi) => ({
    key: 'w' + wi,
    cells: row.map((c) => {
      const x = nightOf(c.k);
      const can = !x.past && c.inMonth;
      const act = x.noms.length > 0;
      const today = c.k === todayK;
      return {
        key: c.k,
        k: c.k,
        n: String(c.n),
        today,
        we: c.we,
        nFs: c.we ? 'clamp(11px,9cqh,16px)' : 'clamp(9px,7cqh,12px)',
        nFsM: c.we ? '9px' : '7px',
        nClr: !c.inMonth || x.past ? 'var(--gt-ghost)' : today ? 'var(--acc)' : c.we || act ? 'var(--gt-text)' : 'var(--gt-text-5)',
        op: (x.past && c.we) || !c.inMonth ? '.5' : '1',
        cur: can ? ('pointer' as const) : ('default' as const),
        z: today ? 2 : 1,
        bd:
          '1px ' +
          ((c.we || today || act) && c.inMonth && !x.past ? 'solid ' : 'dashed ') +
          (today ? 'var(--acc)' : act ? 'var(--gt-border-2)' : c.we && c.inMonth && !x.past ? 'var(--gt-border)' : 'var(--gt-line)'),
        bg: act ? 'var(--gt-tile)' : 'transparent',
        sh: today ? 'var(--gt-shadow-btn)' : 'none',
        sum: x.who.length ? x.who.length + ' IN' : '',
        facesOn: x.who.length > 0,
        faces: x.who.slice(0, 4).map(face),
        qTxt: x.who.length > 4 ? '+' + (x.who.length - 4) : '',
        lines: x.noms.slice(0, 4).map((n) => ({
          key: n.key,
          nm: n.nm,
          ini: n.ini,
          id: n.id,
          fall: n.fall,
          coverCands: n.coverCands,
          range: n.any ? 'ANY' : n.range,
          rClr: n.any ? 'var(--gt-muted-2)' : 'var(--gt-text-5)',
        })),
        moreOn: x.noms.length > 4,
        moreTxt: x.noms.length > 4 ? '+' + (x.noms.length - 4) + ' MORE' : '',
        minis: x.noms.slice(0, 4).map((n) => ({ key: n.key, id: n.id, nm: n.nm, ini: n.ini, fall: n.fall, coverCands: n.coverCands })),
        /** clickable: an upcoming night of this month */
        can,
      };
    }),
  }));
  const monthTxt = SC_MONTH[m] + ' ' + y;
  const tonight = nightOf(todayK);
  const tonightTxt = tonight.noms.length
    ? tonight.noms.length + (tonight.noms.length === 1 ? ' NOMINATION' : ' NOMINATIONS') + ' · ' + tonight.who.length + ' IN'
    : 'NOTHING NOMINATED YET';

  return {
    head: SCH_HEAD,
    weeks,
    monthTxt,
    tonightTxt,
    day: s.schDay ? nightVM(s, s.schDay, nightOf(s.schDay), { now, nowI, meK, roster, face, deco }) : null,
  };
}

type NightOf = { k: string; noms: SchNomVM[]; who: string[]; today: boolean; past: boolean };

/** The night page, the game modal, NOMINATE and the slider (the mirror's `D`). */
function nightVM(
  s: QdStore,
  dk: string,
  day: NightOf,
  c: {
    now: Date;
    nowI: number;
    meK: string;
    roster: readonly User[];
    face: (k: string, i: number) => SchFaceVM;
    deco: (n: SchedNom, k: string) => SchNomVM | null;
  },
) {
  const { now, nowI, meK, roster, face, deco } = c;
  const nowTxt = nowTxtOf(now);
  const dayTxt = scDayTxt(dk);

  const gHours = SC_HRS.map((l, i) => ({
    key: l,
    l,
    left: i * SCH_HPW + 'px',
    leftP: pct(i),
    tx: i === 0 ? 'none' : i === SC_NH ? 'translateX(-100%)' : 'translateX(-50%)',
    st: i % 3 ? ('dashed' as const) : ('solid' as const),
    clr: day.today && Math.floor(nowI) === i ? 'var(--acc)' : i % 3 ? 'var(--gt-ghost)' : 'var(--gt-text-5)',
  }));

  // A row per nominator; a block per nomination across its window, stacked
  // into lanes where one player's windows overlap.
  const rows = roster
    .filter((u) => day.noms.some((n) => n.by === u.k))
    .map((u) => {
      const items = day.noms.filter((n) => n.by === u.k).slice().sort((a, b) => a.fromI - b.fromI);
      const lanes: { lane: number; fromI: number; toI: number }[] = [];
      const blocks = items.map((n) => {
        let lane = 0;
        while (lanes.some((o) => o.lane === lane && o.fromI < n.toI && n.fromI < o.toI)) lane++;
        lanes.push({ lane, fromI: n.fromI, toI: n.toI });
        const wpx = (n.toI - n.fromI) * SCH_HPW - 4;
        return {
          ...n,
          left: n.fromI * SCH_HPW + 'px',
          w: wpx + 'px',
          leftP: pct(n.fromI),
          wP: 'calc(' + (((n.toI - n.fromI) / SC_NH) * 100).toFixed(3) + '% - 4px)',
          top: lane * 60 + 4 + 'px',
          blPx: n.fromI * SCH_HPW,
          bwPx: wpx,
          stripW: Math.min(wpx, 120) + 'px',
          ageOn: wpx >= 150,
          rangeOnM: wpx >= 110,
        };
      });
      const ch = chipOf(u);
      const inNow = day.noms.some((n) => n.live && n.bys.includes(u.k));
      return {
        key: u.k,
        ini: ch.ini,
        bg: ch.hueBg,
        ink: inkOn(ch.hueBg),
        hasAv: ch.hasAv,
        avBg: ch.avBg,
        nm: u.k === meK ? 'YOU' : u.name.toUpperCase(),
        clr: inNow ? 'var(--acc)' : 'var(--gt-text-5)',
        blocks,
        h: Math.max(1, lanes.reduce((mx, o) => Math.max(mx, o.lane + 1), 0)) * 60 + 4 + 'px',
      };
    });

  const onWheel = day.today ? scOnWheel(s.sched[dk] ?? [], now) : [];

  /** The two-handle slider over the hour axis; the ends mean open. */
  const slider = (pk: SchNomVM | null) => {
    const a = s.schFrom == null ? 0 : hourIdx(s.schFrom);
    const b = s.schTo == null ? SC_NH : hourIdx(s.schTo);
    const any = a === 0 && b === SC_NH;
    return {
      a,
      b,
      txt: scRangeTxt(a === 0 ? null : idxHour(a), b === SC_NH ? null : idxHour(b)),
      anyBd: any ? 'var(--acc)' : 'var(--gt-border-2)',
      anyClr: any ? 'var(--acc)' : 'var(--gt-text-5)',
      anyBg: any ? 'var(--accSoft)' : 'transparent',
      aL: pct(a),
      bL: pct(b),
      fillW: (((b - a) / SC_NH) * 100).toFixed(3) + '%',
      aSt: a === 0 ? ('dashed' as const) : ('solid' as const),
      bSt: b === SC_NH ? ('dashed' as const) : ('solid' as const),
      aLbl: a === 0 ? 'OPEN' : SC_HRS[a] ?? '',
      bLbl: b === SC_NH ? 'OPEN' : SC_HRS[b] ?? '',
      aTx: a === 0 ? 'none' : 'translateX(-50%)',
      bTx: b === SC_NH ? 'translateX(-100%)' : 'translateX(-50%)',
      ticks: SC_HRS.map((l, i) => ({ key: l, left: pct(i), st: i % 3 ? ('dashed' as const) : ('solid' as const) })),
      labels: SC_HRS.map((l, i) => ({
        key: l,
        l,
        left: pct(i),
        tx: i === 0 ? 'none' : i === SC_NH ? 'translateX(-100%)' : 'translateX(-50%)',
        vis: i === a || i === b ? ('hidden' as const) : ('visible' as const),
        i,
      })).filter((t) => t.i % 3 === 0 || t.i === SC_NH),
      nm: pk ? pk.nm : 'PICK A GAME',
      nmClr: pk ? 'var(--gt-text)' : 'var(--gt-muted)',
      id: pk ? pk.id : 0,
      ini: pk ? pk.ini : '',
      fall: pk ? pk.fall : '',
      coverCands: pk ? pk.coverCands : ([] as readonly ArtCand[]),
      hasGame: !!pk,
      tLet: pk ? pk.tLet : '',
      tClr: pk ? pk.tClr : 'var(--gt-muted)',
    };
  };

  // ---- the game modal -----------------------------------------------------
  const gN = s.schGame ? day.noms.find((n) => n.id === s.schGame) ?? null : null;
  let g = null as null | ReturnType<typeof gameOf>;
  function gameOf(n: SchNomVM) {
    const e = s.entryOf(n.id);
    const votes = e?.votes ?? {};
    const armed = s.schArm === 'rm';
    const party = roster
      .map((u) => {
        const q = n.bys.includes(u.k);
        const v = votes[u.k];
        const call = v && isCall(v) ? LVL[v] : null;
        const t = face(u.k, 0);
        return {
          ...t,
          q,
          call: call ? call.g : '—',
          cClr: call ? call.c : 'var(--gt-ghost)',
          clr: u.k === meK ? 'var(--acc)' : q ? 'var(--gt-text-3)' : 'var(--gt-text-5)',
          rank: call ? call.v : -1,
        };
      })
      .sort((p, r) => Number(r.q) - Number(p.q) || r.rank - p.rank);
    const game = s.gById(n.id);
    const players = game && game.mx ? (game.mn && game.mn !== game.mx ? game.mn + '–' + game.mx : String(game.mx)) + ' PLAYERS' : '';
    const sub = [players, (game?.tags ?? []).slice(0, 2).join(' · ').toUpperCase(), game?.ea ? 'EARLY ACCESS' : '']
      .filter(Boolean)
      .join(' · ');
    return {
      id: n.id,
      nm: n.nm,
      ini: n.ini,
      fall: n.fall,
      coverCands: n.coverCands,
      tLet: n.tLet,
      tClr: n.tClr,
      sub: sub || 'ON THE PARTY LIST',
      steam: game && game.appid ? 'https://store.steampowered.com/app/' + game.appid : '',
      steamOn: !!(game && game.appid),
      dayTxt,
      byF: n.byF,
      byTxt: n.byTxt,
      rangeFull: n.any ? 'ANY TIME' : n.range,
      rBd: n.live ? 'var(--acc)' : n.any ? 'var(--gt-border)' : 'var(--gt-border-3)',
      rClr: n.live ? 'var(--acc)' : n.any ? 'var(--gt-muted-2)' : 'var(--gt-text-4)',
      qHead: n.cnt + (n.cnt === 1 ? ' QUARTER DOWN' : ' QUARTERS DOWN'),
      party,
      mine: n.mine,
      qBtnOn: !n.mine,
      qMine: n.qMine,
      qLbl: n.qMine ? 'MY QUARTER IS DOWN' : 'PUT MY QUARTER DOWN',
      qBst: n.qMine ? 'solid' : 'dashed',
      qBd: n.qMine ? 'var(--acc)' : 'var(--gt-border-3)',
      qBg: n.qMine ? 'var(--accSoft)' : 'var(--gt-field)',
      qClr: n.qMine ? 'var(--acc)' : 'var(--gt-text-4)',
      editOn: s.schEdit,
      editLbl: s.schEdit ? 'EDITING…' : 'EDIT RANGE',
      rmTip: 'TAKES ' + n.wn + ' OFF ' + dayTxt + ' FOR THE WHOLE PARTY · THE QUARTERS ON IT COME BACK',
      rmArmed: armed,
      rmBd: armed ? 'var(--acc2)' : 'var(--gt-border-2)',
      rmBg: armed ? 'var(--acc2Soft)' : 'transparent',
      rmClr: armed ? 'var(--acc2)' : 'var(--gt-text-4)',
      rmFill: armed ? 0.22 : 0,
      sl: slider(n),
    };
  }
  if (gN) g = gameOf(gN);

  // ---- NOMINATE: GAME NIGHT's rail — search, tier tabs, covers -------------
  const have = day.noms.map((n) => n.id);
  const pqv = s.schQ.trim().toUpperCase();
  const ptv = s.schTier;
  const rank: Record<RoundTier, number> = { s: 0, a: 1, b: 2, c: 3 };
  const shelf = s.list
    .map((e) => {
      const gm = s.gById(e.id);
      return gm ? { g: gm, tier: rTier(e.votes, roster) } : null;
    })
    .filter((x): x is NonNullable<typeof x> => !!x && !have.includes(Number(x.g.appid || x.g.id)) && (!pqv || x.g.n.toUpperCase().includes(pqv)));
  const pool = shelf
    .filter((x) => ptv === 'all' || x.tier === ptv)
    .sort((p, r) => rank[p.tier] - rank[r.tier] || p.g.n.localeCompare(r.g.n));
  const pickG = s.schPick ? s.gById(s.schPick) : null;
  const pk = s.schPick ? deco({ id: s.schPick, by: meK, at: now.getTime(), from: null, to: null, bys: [meK] }, dk) : null;

  return {
    k: dk,
    ttl: dayTxt,
    isToday: day.today,
    notToday: !day.today && !day.past,
    isPast: day.past,
    meta: day.who.length + ' IN · ' + day.noms.length + (day.noms.length === 1 ? ' NOMINATION' : ' NOMINATIONS'),
    nomHead: day.noms.length ? day.noms.length + (day.noms.length === 1 ? ' NOMINATION' : ' NOMINATIONS') : 'NOMINATIONS',
    nomOn: day.noms.length > 0,
    nomEmpty: !day.noms.length,
    gW: SCH_GUT_M + SC_NH * SCH_HPW + 'px',
    gHours,
    nowX: nowI * SCH_HPW + 'px',
    nowXP: pct(nowI),
    nowPill: nowTxt,
    nowOn: day.today,
    /** where the phone's guide opens: NOW, less a little lead-in */
    openAt: day.today ? Math.max(0, nowI * SCH_HPW + SCH_GUT_M - 160) : 0,
    rows,
    startOn: day.today,
    startVar: onWheel.length ? ('solid' as const) : ('outline' as const),
    startSub: s.round
      ? 'GAME NIGHT’S ON — OPENS IT'
      : onWheel.length
        ? onWheel.length + ' ON THE WHEEL · ' + nowTxt
        : 'NOTHING CROSSES THE LINE · ' + nowTxt,
    laterTxt: day.past ? 'THIS NIGHT HAS PASSED.' : 'START GAME NIGHT APPEARS HERE ON ' + dayTxt + '.',
    nomOpen: s.schNomOpen,
    addOp: s.schPick ? 1 : 0.38,
    addOk: !!s.schPick,
    pq: s.schQ,
    tabs: (
      [
        ['all', 'ALL'],
        ['s', 'S'],
        ['a', 'A'],
        ['b', 'B'],
        ['c', 'C'],
      ] as const
    ).map(([key, t], i) => {
      const on = ptv === key;
      const cnt = shelf.filter((x) => key === 'all' || x.tier === key).length;
      return {
        key,
        t,
        n: String(cnt),
        ml: i ? '-1px' : '0',
        z: on ? 2 : 1,
        bd: on ? 'var(--acc)' : 'var(--gt-border-2)',
        bg: on ? 'var(--accSoft)' : 'transparent',
        clr: on ? 'var(--acc)' : 'var(--gt-text-5)',
        nClr: on ? 'var(--acc)' : 'var(--gt-faint)',
      };
    }),
    pool: pool.map((x) => {
      const id = Number(x.g.appid || x.g.id);
      const on = s.schPick === id;
      return {
        key: String(x.g.id),
        id,
        nm: x.g.n.toUpperCase(),
        ini: initialsOf(x.g.n),
        fall: fallGradient(x.g.id),
        coverCands: artCands(x.g, 'p', s.assets),
        tLet: x.tier.toUpperCase(),
        tClr: TCLR[x.tier],
        on,
        bd: on ? 'var(--acc)' : 'var(--gt-border-2)',
        sh: on ? 'var(--gt-shadow-btn)' : 'none',
      };
    }),
    poolEmpty: !pool.length,
    poolEmptyTxt: shelf.length
      ? 'NO MATCHES — CLEAR THE SEARCH OR PICK ANOTHER TAB'
      : have.length
        ? 'EVERYTHING ON THE LIST IS ALREADY NOMINATED'
        : 'THE PARTY LIST IS EMPTY — ADD GAMES FROM DISCOVER FIRST',
    pickTxt: pickG ? pickG.n.toUpperCase() : 'PICK A GAME',
    pickClr: s.schPick ? 'var(--gt-text)' : 'var(--gt-muted-2)',
    sl: slider(pk),
    gOn: !!g,
    g,
  };
}

export type ScheduleVM = ReturnType<typeof scheduleVM>;
export type SchNightVM = NonNullable<ScheduleVM['day']>;
export type SchGameVM = NonNullable<SchNightVM['g']>;
export type SchSliderVM = SchNightVM['sl'];

/** The slider's pointer → an index on the axis, for both trees' Range Slider. */
export function sliderIdxAt(clientX: number, rect: { left: number; width: number }): number {
  if (!rect.width) return 0;
  return Math.max(0, Math.min(SC_NH, Math.round(((clientX - rect.left) / rect.width) * SC_NH)));
}

/**
 * Which handle a press at index `i` grabs, and the range it leaves — the
 * mirror's `down` / `apply`: the nearer handle, ties to the far one past the
 * upper bound; a handle never crosses or meets the other.
 */
export function sliderApply(h: 'a' | 'b', i: number, a: number, b: number): { from: number | null; to: number | null } | null {
  let na = a;
  let nb = b;
  if (h === 'a') {
    na = Math.min(i, b - 1);
    if (na === a) return null;
  } else {
    nb = Math.max(i, a + 1);
    if (nb === b) return null;
  }
  return { from: na === 0 ? null : idxHour(na), to: nb === SC_NH ? null : idxHour(nb) };
}

export function sliderGrab(i: number, a: number, b: number): 'a' | 'b' {
  return Math.abs(i - a) <= Math.abs(i - b) ? (i >= b ? 'b' : 'a') : 'b';
}
